import {
	Button,
	CloseButton,
	DatePicker,
	Drawer,
	Field,
	Flex,
	Grid,
	Input,
	InputGroup,
	InputNationalIdentity,
	InputPhone,
	InputSurname,
	NO_SURNAME_VALUE,
	Portal,
	Select,
	Separator,
	Show,
	Text,
} from '@welbe/design-system';
import { CustomSelectField } from 'App/Components/CustomSelect/CustomSelectField';
import {
	MedicalEnums,
	TitleSelector,
} from 'App/Components/TitleSelector/TitleSelector';
import { GENERIC_CURP } from 'constants/genericNationalIdentity';
import { sexOptions } from 'constants/sex';
import { Controller } from 'react-hook-form';
import { FiUserPlus } from 'react-icons/fi';
import {
	MEXICO_COUNTRY_CODE,
	UNSPECIFIED_ENTITY_CODE,
} from './DoctorDrawer.constants';
import { useDoctorDrawer } from './DoctorDrawer.hooks';
import { DoctorDrawerProps } from './DoctorDrawer.types';

export const DoctorDrawer = ({
	open,
	setOpen,
	user,
}: DoctorDrawerProps): JSX.Element => {
	const {
		isEditingDoctor,
		fieldsWithErrors,
		isRequestLoading,
		register,
		control,
		setValue,
		watch,
		personalTypes,
		isPersonalTypesLoading,
		specialties,
		isSpecialtiesLoading,
		mappedBranchOfficeOptions,
		isLoading,
		isBranchOfficesLoading,
		handleSaveDoctor,
		countries,
		isCountriesLoading,
		federalEntities,
		isFederalEntitiesLoading,
		hasSires,
		nationalities,
		isNationalitiesLoading,
		residenceCountries,
		isResidenceCountriesLoading,
		residenceFederalEntities,
		isResidenceFederalEntitiesLoading,
		municipalities,
		isMunicipalitiesLoading,
		localities,
		isLocalitiesLoading,
		isMexico,
		isSpecialEntity,
		isBirthCountryMexico,
		isCurpIgnored,
		canCheckNoCurp,
		handleCountryCodeChange,
		handleFederalEntityResidenceChange,
		handleMunicipalityResidenceChange,
	} = useDoctorDrawer({ open, setOpen, user });

	return (
		<Drawer.Root
			open={open}
			size="lg"
			onOpenChange={(details: { open: boolean }) => setOpen(details.open)}
		>
			<Portal>
				<Drawer.Backdrop />
				<Drawer.Positioner padding="4">
					<Drawer.Content rounded="md" key={1}>
						<Drawer.Header rounded="md" bg="gray.50" p="1rem">
							<Flex gap={3} align="center">
								<Flex
									justify="center"
									align="center"
									bg="blue.500"
									borderRadius="md"
									width="50px"
									height="50px"
									flexShrink={0}
								>
									<FiUserPlus size={24} color="white" />
								</Flex>
								<Flex direction="column" gap={1}>
									<Drawer.Title>
										<Show
											when={isEditingDoctor}
											fallback="Agregar personal médico"
										>
											Actualizar personal médico
										</Show>
									</Drawer.Title>
									<Text color="gray.600" fontSize="14px">
										Registra los datos del personal médico de tu organización
									</Text>
								</Flex>
							</Flex>
						</Drawer.Header>
						<Separator />
						<Drawer.Body p="2rem 1rem" overflowY="auto">
							<Text fontSize={16} fontWeight={500}>
								Datos personales
							</Text>
							<Grid
								templateColumns="1fr 1fr"
								direction="column"
								gap={4}
								margin="2rem 0"
							>
								<Field.Root
									required
									invalid={
										!!fieldsWithErrors['firstName'] ||
										!!fieldsWithErrors['title']
									}
									disabled={isRequestLoading}
								>
									<Field.Label>
										Nombre <Field.RequiredIndicator />
									</Field.Label>
									<InputGroup
										flex="1"
										startElement={
											<TitleSelector
												invalid={!!fieldsWithErrors['title']}
												value={watch('title') as MedicalEnums}
												onChange={(title: MedicalEnums) =>
													setValue('title', title, { shouldValidate: true })
												}
											/>
										}
										css={{
											'& > div': {
												pointerEvents: 'unset',
												paddingInline: '0.25rem',
											},
										}}
									>
										<Input
											{...register('firstName')}
											ps="4.85rem"
											pe="0"
											placeholder="Escribe el nombre"
											background="white"
											required
											disabled={isRequestLoading}
										/>
									</InputGroup>
									<Field.ErrorText>
										{fieldsWithErrors['firstName'] ?? fieldsWithErrors['title']}
									</Field.ErrorText>
								</Field.Root>

								<Controller
									name="middleName"
									control={control}
									render={({ field }) => (
										<InputSurname.Snippet
											required
											label="Primer Apellido"
											placeholder="Escribe el primer apellido"
											checkboxText="No cuenta con primer apellido"
											invalid={!!fieldsWithErrors['middleName']}
											errorText={fieldsWithErrors['middleName']}
											disabled={isRequestLoading}
											value={field.value || ''}
											onChange={(value) =>
												field.onChange(
													value === NO_SURNAME_VALUE ? undefined : value,
												)
											}
											checked={Boolean(watch('hasNoMiddleName'))}
											onCheckedChange={(checked) => {
												setValue('hasNoMiddleName', checked);
												if (checked) field.onChange('');
											}}
										/>
									)}
								/>

								<Controller
									name="lastName"
									control={control}
									render={({ field }) => (
										<InputSurname.Snippet
											required
											label="Segundo Apellido"
											placeholder="Escribe el segundo apellido"
											checkboxText="No cuenta con segundo apellido"
											invalid={!!fieldsWithErrors['lastName']}
											errorText={fieldsWithErrors['lastName']}
											disabled={isRequestLoading}
											value={field.value || ''}
											onChange={(value) =>
												field.onChange(
													value === NO_SURNAME_VALUE ? undefined : value,
												)
											}
											checked={Boolean(watch('hasNoLastName'))}
											onCheckedChange={(checked) => {
												setValue('hasNoLastName', checked);
												if (checked) field.onChange('');
											}}
										/>
									)}
								/>

								<Controller
									name="birthDate"
									control={control}
									render={({ field }) => (
										<DatePicker.Snippet
											label="Fecha de nacimiento"
											required
											disabled={isRequestLoading}
											invalid={!!fieldsWithErrors['birthDate']}
											errorText={fieldsWithErrors['birthDate']}
											value={field.value}
											usePortal
											onChange={(value) => field.onChange(value)}
										/>
									)}
								/>

								<Controller
									name="sex"
									control={control}
									render={({ field }) => (
										<Select.Snippet
											disabled={isRequestLoading}
											label="Sexo"
											placeholder="Seleccione sexo"
											invalid={!!fieldsWithErrors['sex']}
											required
											controlProps={{ border: '0 none' }}
											showEmpty
											onValueChange={(data: any) => field.onChange(data?.id)}
											value={field.value ? [field.value] : []}
											options={sexOptions}
											errorText={fieldsWithErrors['sex']}
										/>
									)}
								/>

								<Controller
									name="countryCode"
									control={control}
									render={({ field }) => (
										<CustomSelectField
											label="Pais de nacimiento"
											required
											disabled={isRequestLoading}
											error={fieldsWithErrors['countryCode']}
											options={countries}
											value={field.value}
											onChange={handleCountryCodeChange}
											placeholder="Selecciona un pais"
											loading={
												isCountriesLoading ||
												(!!field.value && countries.length === 0)
											}
										/>
									)}
								/>

								<Controller
									name="federalEntityCode"
									control={control}
									render={({ field }) => (
										<CustomSelectField
											label="Entidad de nacimiento"
											required
											disabled={isRequestLoading || !isBirthCountryMexico}
											error={fieldsWithErrors['federalEntityCode']}
											options={federalEntities}
											value={field.value}
											onChange={field.onChange}
											placeholder="Selecciona una entidad de nacimiento"
											loading={
												isFederalEntitiesLoading ||
												(!!field.value && federalEntities.length === 0)
											}
										/>
									)}
								/>

								<Controller
									name="nationalityCode"
									control={control}
									render={({ field }) => (
										<CustomSelectField
											label="Nacionalidad"
											required
											disabled={isRequestLoading}
											error={fieldsWithErrors['nationalityCode']}
											options={nationalities}
											value={field.value}
											onChange={field.onChange}
											placeholder="Selecciona la nacionalidad"
											loading={
												isNationalitiesLoading ||
												(!!field.value && nationalities.length === 0)
											}
										/>
									)}
								/>

								<Controller
									name="nationalIdentity"
									control={control}
									render={({ field }) => (
										<InputNationalIdentity
											label="CURP del personal médico"
											background="white"
											name="nationalIdentity"
											disabled={isRequestLoading || isCurpIgnored}
											checkboxDisabled={!canCheckNoCurp}
											invalid={!!fieldsWithErrors['nationalIdentity']}
											invalidMessage={fieldsWithErrors['nationalIdentity']}
											value={field.value}
											onChange={(value) => field.onChange(value)}
											checked={field.value === GENERIC_CURP}
											onCheckedChange={(checked) =>
												field.onChange(checked ? GENERIC_CURP : '')
											}
										/>
									)}
								/>

								<Controller
									name="contactNumber"
									control={control}
									render={({ field }) => (
										<InputPhone
											required
											invalid={!!fieldsWithErrors['contactNumber']}
											value={field.value}
											onChange={(value: any) => {
												field.onChange(value);
											}}
										/>
									)}
								/>
							</Grid>

							<Text fontSize={16} fontWeight={500}>
								Datos de residencia
							</Text>
							<Grid
								templateColumns="1fr 1fr"
								direction="column"
								gap={4}
								margin="2rem 0"
							>
								<Controller
									name="countryResidenceCode"
									control={control}
									render={({ field }) => (
										<CustomSelectField
											label="Pais de residencia"
											required={hasSires}
											disabled={isRequestLoading}
											error={fieldsWithErrors['countryResidenceCode']}
											options={residenceCountries}
											value={field.value}
											onChange={(v) => {
												field.onChange(v);
												if (Number(v) !== MEXICO_COUNTRY_CODE) {
													handleFederalEntityResidenceChange(
														UNSPECIFIED_ENTITY_CODE,
													);
												}
											}}
											placeholder="Seleccione una opción"
											loading={
												isResidenceCountriesLoading ||
												(!!field.value && residenceCountries.length === 0)
											}
										/>
									)}
								/>
								<Controller
									name="federalEntityResidenceCode"
									control={control}
									render={({ field }) => (
										<CustomSelectField
											label="Entidad de residencia"
											required={hasSires}
											disabled={isRequestLoading || !isMexico}
											error={fieldsWithErrors['federalEntityResidenceCode']}
											options={residenceFederalEntities}
											value={field.value}
											onChange={(v) =>
												handleFederalEntityResidenceChange(Number(v))
											}
											placeholder="Seleccione una opción"
											loading={
												isResidenceFederalEntitiesLoading ||
												(!!field.value && residenceFederalEntities.length === 0)
											}
										/>
									)}
								/>
								<Controller
									name="municipalityResidenceCode"
									control={control}
									render={({ field }) => (
										<CustomSelectField
											label="Municipio de residencia"
											required={hasSires}
											disabled={
												isRequestLoading || !isMexico || isSpecialEntity
											}
											error={fieldsWithErrors['municipalityResidenceCode']}
											options={municipalities}
											value={field.value}
											onChange={(v) =>
												handleMunicipalityResidenceChange(Number(v))
											}
											placeholder="Seleccione una opción"
											loading={
												isMunicipalitiesLoading ||
												(!isSpecialEntity &&
													!!field.value &&
													municipalities.length === 0)
											}
										/>
									)}
								/>
								<Controller
									name="localityResidenceCode"
									control={control}
									render={({ field }) => (
										<CustomSelectField
											label="Localidad de residencia"
											required={hasSires}
											disabled={
												isRequestLoading || !isMexico || isSpecialEntity
											}
											error={fieldsWithErrors['localityResidenceCode']}
											options={localities}
											value={field.value}
											onChange={field.onChange}
											placeholder="Selecciona una localidad"
											loading={
												isLocalitiesLoading ||
												(!isSpecialEntity &&
													!!field.value &&
													localities.length === 0)
											}
										/>
									)}
								/>
							</Grid>

							<Text fontSize={16} fontWeight={500}>
								Perfil médico
							</Text>
							<Grid
								templateColumns="1fr 1fr"
								direction="column"
								gap={4}
								margin="2rem 0"
							>
								<Field.Root
									invalid={!!fieldsWithErrors['professionalLicenseId']}
									disabled={isRequestLoading}
									required
								>
									<Field.Label>
										Cedula profesional <Field.RequiredIndicator />
									</Field.Label>
									<Input
										{...register('professionalLicenseId')}
										placeholder="Escribe la cedula profesional"
										background="white"
										required
										disabled={isRequestLoading}
									/>
									<Field.ErrorText>
										{fieldsWithErrors['professionalLicenseId']}
									</Field.ErrorText>
								</Field.Root>

								<Field.Root
									invalid={!!fieldsWithErrors['university']}
									disabled={isRequestLoading}
								>
									<Field.Label>Universidad</Field.Label>
									<Input
										{...register('university')}
										placeholder="Escribe la universidad"
										background="white"
										required
										disabled={isRequestLoading}
									/>
									<Field.ErrorText>
										{fieldsWithErrors['university']}
									</Field.ErrorText>
								</Field.Root>

								<Controller
									name="doctorPersonalTypeId"
									control={control}
									render={({ field }) => (
										<Select.Snippet
											disabled={isRequestLoading}
											label="Tipo de profesional médico"
											placeholder="Seleccione tipo de profesional médico"
											invalid={!!fieldsWithErrors['doctorPersonalTypeId']}
											required
											controlProps={{ border: '0 none' }}
											loading={
												isPersonalTypesLoading ||
												(!!field.value && personalTypes.length === 0)
											}
											showEmpty
											onValueChange={(data: any) => field.onChange(data?.id)}
											value={field.value ? [field.value] : []}
											options={personalTypes}
											errorText={fieldsWithErrors['doctorPersonalTypeId']}
										/>
									)}
								/>
								<Controller
									name="specialties"
									control={control}
									render={({ field }) => (
										<Select.Snippet
											disabled={isRequestLoading}
											label="Especialidad"
											placeholder="Seleccione especialidad"
											invalid={!!fieldsWithErrors['specialtyIds']}
											required={!hasSires}
											controlProps={{ border: '0 none' }}
											loading={
												isSpecialtiesLoading ||
												(!!field.value?.length && specialties.length === 0)
											}
											showEmpty
											multiple
											onValueChange={(data: any) => field.onChange(data || [])}
											value={
												field.value?.map(({ id }) => id).filter(Boolean) ?? []
											}
											options={specialties}
											errorText={fieldsWithErrors['specialtyIds']}
										/>
									)}
								/>
							</Grid>

							<Text fontSize={16} fontWeight={500}>
								Correo y sucursales asignadas
							</Text>
							<Grid
								templateColumns="1fr 1fr"
								direction="column"
								gap={4}
								margin="2rem 0"
							>
								<Field.Root
									invalid={!!fieldsWithErrors['email']}
									disabled={isRequestLoading}
									required
								>
									<Field.Label>
										Correo <Field.RequiredIndicator />
									</Field.Label>
									<Input
										{...register('email')}
										placeholder="Escribe el correo"
										background="white"
										required
										disabled={isRequestLoading}
									/>
									<Field.ErrorText>{fieldsWithErrors['email']}</Field.ErrorText>
								</Field.Root>
								<Controller
									name="branchOffices"
									control={control}
									render={({ field }) => (
										<Select.Snippet
											disabled={isRequestLoading}
											label="Sucursales"
											placeholder="Seleccione sucursales"
											invalid={!!fieldsWithErrors['branchOfficeIds']}
											required
											loading={
												isLoading ||
												isBranchOfficesLoading ||
												(!!field.value?.length &&
													mappedBranchOfficeOptions.length === 0)
											}
											controlProps={{ border: '0 none' }}
											multiple
											showEmpty
											onValueChange={(data) => field.onChange(data || [])}
											value={
												field.value?.map(({ id }) => id).filter(Boolean) ?? []
											}
											options={mappedBranchOfficeOptions}
											errorText={fieldsWithErrors['branchOfficeIds']}
										/>
									)}
								/>
							</Grid>
						</Drawer.Body>
						<Separator />
						<Drawer.Footer p="1rem" rounded="md">
							<Button
								variant="outline"
								onClick={() => setOpen(false)}
								disabled={isRequestLoading}
							>
								Cancelar
							</Button>
							<Button onClick={handleSaveDoctor} loading={isRequestLoading}>
								Guardar
							</Button>
						</Drawer.Footer>
						<Drawer.CloseTrigger asChild>
							<CloseButton size="sm" />
						</Drawer.CloseTrigger>
					</Drawer.Content>
				</Drawer.Positioner>
			</Portal>
		</Drawer.Root>
	);
};
